// Menu Assistência (pedido 2026-10-16: "ver gráficos dos guinchos, quantos
// foram pedidos, aba com cadastro dos guinchos, mapa com a localização dos
// guinchos por cidade"). Só admin/funcionário — backend src/assistance-center/.
// As solicitações vêm do "Registrar Assistência" do app; aqui a equipe
// atribui o guincho, o valor e fecha o atendimento.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AdvancedMarker,
  APIProvider,
  InfoWindow,
  Map as GoogleMap,
  useMap as useGoogleMap,
} from "@vis.gl/react-google-maps";
import {
  CalendarClock,
  CheckCircle2,
  Clock,
  Crosshair,
  DollarSign,
  Gauge,
  Loader2,
  MapPin,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Trash2,
  Truck,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GpsInlineLoader } from "@/components/gps-loader";
import { AssistanceClientsTab } from "@/components/assistance-clients-tab";
import { AssistanceNewRequestDialog } from "@/components/assistance-new-request-dialog";
import { AssistanceDispatchPanel } from "@/components/assistance-dispatch-panel";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { GOOGLE_MAPS_CLEAN_STYLE, hasCustomGoogleMapId, resolveGoogleMapId } from "@/lib/google-maps-style";
import { useVehicles } from "@/hooks/queries";
import {
  useAssistanceCenterAccess,
  useAssistanceRequests,
  useAssistanceSummary,
  useDeleteTowTruck,
  useSaveTowTruck,
  useTowTrucks,
  useUpdateAssistanceRequest,
  useCancelAssistanceRequest,
} from "@/hooks/queries/assistance-center";
import {
  gpsDriveAssistanceCenterService,
  REQUEST_STATUSES,
  TRUCK_TYPES,
  type AssistanceCenterAccess,
  type AssistanceRequestRow,
  type AssistanceSummary,
  type RequestStatus,
  type TowTruck,
  type TowTruckInput,
  type TruckType,
} from "@/services/gpsdrive-assistance-center.service";
import type { Vehicle } from "@/types";
import { LicensePlate } from "@/components/license-plate";

export const Route = createFileRoute("/assistencia")({
  component: AssistancePageGate,
  head: () => ({ meta: [{ title: "Assistência — GPS Drive" }] }),
});

// ---------------------------------------------------------------------------
// Formatação

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const fmt = (n: number) => brl.format(n);
const fmtNum = (n: number, d = 0) => n.toLocaleString("pt-BR", { maximumFractionDigits: d });
const fmtDate = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const fmtMonth = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1]}/${ym.slice(2, 4)}`;
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const fmtDuration = (min: number) => (min < 60 ? `${Math.round(min)} min` : min < 1440 ? `${fmtNum(min / 60, 1)} h` : `${fmtNum(min / 1440, 1)} dias`);
const digits = (s: string | null) => (s ?? "").replace(/\D/g, "");
const waLink = (s: string | null) => {
  const d = digits(s);
  if (d.length < 10) return null;
  return `https://wa.me/${d.length <= 11 ? `55${d}` : d}`;
};
const truckTypeLabel = (k: string) => TRUCK_TYPES.find((t) => t.key === k)?.label ?? k;
const statusLabel = (k: string) => REQUEST_STATUSES.find((s) => s.key === k)?.label ?? k;
const STATUS_COLOR: Record<string, string> = {
  aberto: "var(--asst-open)",
  em_andamento: "var(--asst-progress)",
  concluido: "var(--asst-done)",
  cancelado: "var(--asst-neutral)",
};
// Paleta categórica (tipos de serviço).
const SERIES = ["var(--asst-c1)", "var(--asst-c2)", "var(--asst-c3)", "var(--asst-c4)", "var(--asst-c5)", "var(--asst-c6)"];

function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

type PresetKey = "mes" | "mes_passado" | "3m" | "6m" | "12m" | "ano" | "custom";
const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "mes", label: "Este mês" },
  { key: "mes_passado", label: "Mês passado" },
  { key: "3m", label: "3 meses" },
  { key: "6m", label: "6 meses" },
  { key: "12m", label: "12 meses" },
  { key: "ano", label: "Este ano" },
  { key: "custom", label: "Personalizado" },
];

function presetRange(key: Exclude<PresetKey, "custom">): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const end = (yy: number, mm: number) => new Date(yy, mm + 1, 0);
  switch (key) {
    case "mes":
      return { from: isoLocal(new Date(y, m, 1)), to: isoLocal(end(y, m)) };
    case "mes_passado":
      return { from: isoLocal(new Date(y, m - 1, 1)), to: isoLocal(end(y, m - 1)) };
    case "3m":
      return { from: isoLocal(new Date(y, m - 2, 1)), to: isoLocal(end(y, m)) };
    case "6m":
      return { from: isoLocal(new Date(y, m - 5, 1)), to: isoLocal(end(y, m)) };
    case "12m":
      return { from: isoLocal(new Date(y, m - 11, 1)), to: isoLocal(end(y, m)) };
    case "ano":
      return { from: isoLocal(new Date(y, 0, 1)), to: isoLocal(new Date(y, 11, 31)) };
  }
}

const errorText = (e: unknown, fallback: string) => (e instanceof GpsDriveApiError ? e.message : fallback);

// ---------------------------------------------------------------------------
// Página

function AssistancePageGate() {
  const { data: access, isLoading } = useAssistanceCenterAccess();
  if (isLoading) return <GpsInlineLoader className="h-[70vh]" />;
  if (!access?.view) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você não tem acesso à Assistência. Peça a um administrador para liberar em Usuários → Editar → Permissões →
            Assistência (guinchos).
          </p>
        </div>
      </div>
    );
  }
  return <AssistancePage access={access} />;
}

type TabKey = "painel" | "solicitacoes" | "clientes" | "guinchos" | "mapa";

function AssistancePage({ access }: { access: AssistanceCenterAccess }) {
  const [preset, setPreset] = useState<PresetKey>("mes");
  const [custom, setCustom] = useState(() => presetRange("mes"));
  const range = preset === "custom" ? custom : presetRange(preset);
  const [tab, setTab] = useState<TabKey>("painel");
  const summary = useAssistanceSummary(range.from, range.to);
  const trucks = useTowTrucks();
  const [truckDialog, setTruckDialog] = useState<{ editing: TowTruck | null } | null>(null);
  const [newRequest, setNewRequest] = useState(false);
  const [dispatchFor, setDispatchFor] = useState<string | null>(null);

  const tabs: { key: TabKey; label: string; badge?: number }[] = [
    { key: "painel", label: "Painel" },
    { key: "solicitacoes", label: "Solicitações", badge: summary.data?.kpis.open },
    { key: "clientes", label: "Clientes" },
    { key: "guinchos", label: "Guinchos", badge: trucks.data?.length },
    { key: "mapa", label: "Mapa" },
  ];
  const periodMatters = tab === "painel" || tab === "solicitacoes" || tab === "mapa";

  return (
    <div className="asst-root min-h-full bg-muted/30">
      <div className="mx-auto max-w-[1400px] space-y-5 p-4 md:p-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-[color-mix(in_srgb,var(--asst-c1)_15%,transparent)] text-lg">
                🛻
              </span>
              Assistência
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">Guinchos, solicitações de assistência, custos e cobertura por cidade</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {access.manage && (
              <Button variant="outline" onClick={() => setTruckDialog({ editing: null })}>
                <Plus className="h-4 w-4" /> Cadastrar guincho
              </Button>
            )}
            {access.requests && (
              <Button onClick={() => setNewRequest(true)} className="bg-[var(--asst-c2)] text-white hover:opacity-90">
                <Truck className="h-4 w-4" /> Nova assistência
              </Button>
            )}
          </div>
        </header>

        {periodMatters && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-background p-2">
            <CalendarClock className="ml-1 h-4 w-4 text-muted-foreground" />
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
                    preset === p.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-1.5 text-xs">
                <input
                  type="date"
                  value={custom.from}
                  onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))}
                  className="h-8 rounded-md border bg-background px-2"
                />
                até
                <input
                  type="date"
                  value={custom.to}
                  onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))}
                  className="h-8 rounded-md border bg-background px-2"
                />
              </div>
            )}
            <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
              {fmtDate(range.from)} – {fmtDate(range.to)}
            </span>
            <Button variant="ghost" size="sm" onClick={() => void summary.refetch()} disabled={summary.isFetching}>
              <RefreshCw className={cn("h-4 w-4", summary.isFetching && "animate-spin")} />
              Atualizar
            </Button>
          </div>
        )}

        <div className="flex gap-1 overflow-x-auto border-b">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors",
                tab === t.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
              {!!t.badge && (
                <span
                  className={cn(
                    "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px]",
                    t.key === "solicitacoes" ? "bg-[var(--asst-open)] text-white" : "bg-muted text-muted-foreground",
                  )}
                >
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "painel" &&
          (summary.isLoading ? (
            <LoadingBlock text="Carregando o painel…" />
          ) : summary.error && !summary.data ? (
            <ErrorBlock text={errorText(summary.error, "Não foi possível carregar o painel.")} />
          ) : summary.data ? (
            <OverviewTab summary={summary.data} onGoTo={setTab} />
          ) : null)}
        {tab === "solicitacoes" && <RequestsTab from={range.from} to={range.to} access={access} trucks={trucks.data ?? []} onDispatch={setDispatchFor} />}
        {tab === "clientes" && <AssistanceClientsTab canManage={access.manage} />}
        {tab === "guinchos" && (
          <TowTrucksTab
            trucks={trucks.data}
            loading={trucks.isLoading}
            error={trucks.error}
            access={access}
            onNew={() => setTruckDialog({ editing: null })}
            onEdit={(t) => setTruckDialog({ editing: t })}
          />
        )}
        {tab === "mapa" && <MapTab trucks={trucks.data ?? []} from={range.from} to={range.to} />}
      </div>

      {truckDialog && <TowTruckDialog editing={truckDialog.editing} onClose={() => setTruckDialog(null)} />}
      {newRequest && (
        <AssistanceNewRequestDialog
          onClose={() => setNewRequest(false)}
          onCreated={(id, isTow) => {
            setNewRequest(false);
            setTab("solicitacoes");
            if (isTow) setDispatchFor(id);
          }}
        />
      )}
      {dispatchFor && <AssistanceDispatchPanel requestId={dispatchFor} onClose={() => setDispatchFor(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blocos visuais

function LoadingBlock({ text }: { text: string }) {
  return (
    <div className="flex h-60 items-center justify-center gap-2 rounded-2xl border bg-background text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> {text}
    </div>
  );
}

function ErrorBlock({ text }: { text: string }) {
  return <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">{text}</div>;
}

function Kpi({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: ReactNode;
  accent: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border bg-background p-4 shadow-sm">
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: accent }} />
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <span className="grid h-7 w-7 place-items-center rounded-lg" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
          <span style={{ color: accent }}>
            <Icon className="h-4 w-4" />
          </span>
        </span>
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Panel({ title, subtitle, action, children, className }: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-2xl border bg-background p-4 shadow-sm", className)}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function EmptyChart({ text }: { text: string }) {
  return <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">{text}</div>;
}

function PlanTag({ plan }: { plan?: string | null }) {
  if (!plan) return null;
  return (
    <span className="ml-1.5 inline-flex items-center rounded-full bg-[color-mix(in_srgb,var(--asst-done)_15%,transparent)] px-1.5 py-0.5 align-middle text-[10px] font-bold text-[var(--asst-done)]">
      🛡 {plan}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: `color-mix(in srgb, ${STATUS_COLOR[status] ?? "gray"} 15%, transparent)`, color: STATUS_COLOR[status] }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_COLOR[status] }} />
      {statusLabel(status)}
    </span>
  );
}

const axisTick = { fontSize: 11, fill: "var(--asst-axis)" };

function CountTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string; dataKey: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-md">
      {label && <div className="mb-1 font-semibold">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="ml-auto font-semibold tabular-nums">{p.dataKey === "custo" || p.dataKey === "cost" ? fmt(p.value) : fmtNum(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// Barras horizontais simples (cidades, guinchos) — legível no celular sem
// eixo cortado, e com o número na ponta.
function RankBars({ data, color, empty, showCost }: { data: { name: string; count: number; cost: number }[]; color: string; empty: string; showCost?: boolean }) {
  if (data.length === 0) return <EmptyChart text={empty} />;
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <ul className="space-y-2">
      {data.map((d) => (
        <li key={d.name} className="text-xs">
          <div className="mb-0.5 flex items-center justify-between gap-2">
            <span className="truncate font-medium" title={d.name}>
              {d.name}
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              <span className="font-semibold text-foreground">{d.count}</span>
              {showCost && d.cost > 0 && ` · ${fmt(d.cost)}`}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full" style={{ width: `${(d.count / max) * 100}%`, background: color }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Aba Painel

function OverviewTab({ summary, onGoTo }: { summary: AssistanceSummary; onGoTo: (t: TabKey) => void }) {
  const k = summary.kpis;
  const months = summary.byMonth.map((m) => ({ ...m, label: fmtMonth(m.month), outros: m.total - m.guincho }));
  const services = summary.byService.map((s, i) => ({ ...s, color: SERIES[i % SERIES.length] }));
  const statuses = summary.byStatus.map((s) => ({ ...s, label: statusLabel(s.name), color: STATUS_COLOR[s.name] ?? "var(--asst-neutral)" }));
  const hours = summary.byHour.map((h) => ({ ...h, label: `${String(h.hour).padStart(2, "0")}h` }));
  const weekdays = summary.byWeekday.map((d) => ({ ...d, label: WEEKDAYS[d.day] }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi icon={Wrench} label="Solicitações no período" value={fmtNum(k.requests)} hint={`${k.uniqueVehicles} veículos atendidos`} accent="var(--asst-c1)" />
        <Kpi icon={Truck} label="Guinchos acionados" value={fmtNum(k.towRequests)} hint={k.requests ? `${fmtNum((k.towRequests / k.requests) * 100)}% das solicitações` : "—"} accent="var(--asst-c2)" />
        <Kpi icon={Clock} label="Em aberto" value={fmtNum(k.open)} hint={`${k.completed} concluídas · ${k.canceled} canceladas`} accent="var(--asst-open)" />
        <Kpi icon={DollarSign} label="Gasto com guinchos" value={fmt(k.towCost)} hint={k.totalCost !== k.towCost ? `${fmt(k.totalCost)} em toda a assistência` : "no período"} accent="var(--asst-c3)" />
        <Kpi icon={DollarSign} label="Custo médio do guincho" value={k.avgTowCost != null ? fmt(k.avgTowCost) : "—"} hint="dos atendimentos com valor" accent="var(--asst-c3)" />
        <Kpi icon={Gauge} label="Distância média" value={k.avgTowKm != null ? `${fmtNum(k.avgTowKm, 1)} km` : "—"} hint="por guincho acionado" accent="var(--asst-c4)" />
        <Kpi icon={CheckCircle2} label="Tempo médio até concluir" value={k.avgResolutionMin != null ? fmtDuration(k.avgResolutionMin) : "—"} hint="da solicitação ao fechamento" accent="var(--asst-done)" />
        <Kpi icon={MapPin} label="Guinchos cadastrados" value={fmtNum(k.towTrucks)} hint={`${k.activeTowTrucks} ativos · ${k.cities} cidades`} accent="var(--asst-c5)" />
      </div>

      <Panel title="Solicitações por mês" subtitle="Guinchos acionados × demais serviços de assistência">
        {k.requests === 0 ? (
          <EmptyChart text="Nenhuma solicitação no período" />
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} barCategoryGap="28%" margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--asst-grid)" />
                <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
                <Tooltip content={<CountTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="guincho" name="Guincho" stackId="a" fill="var(--asst-c2)" maxBarSize={36} />
                <Bar dataKey="outros" name="Outros serviços" stackId="a" fill="var(--asst-c1)" radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel title="Por tipo de serviço" subtitle="Quantas vezes cada serviço foi pedido">
          {services.length === 0 ? (
            <EmptyChart text="Sem dados no período" />
          ) : (
            <div className="flex items-center gap-4">
              <div className="relative h-40 w-40 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={services} dataKey="count" nameKey="name" innerRadius="62%" outerRadius="100%" paddingAngle={2} stroke="hsl(var(--background))" strokeWidth={2}>
                      {services.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CountTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-bold tabular-nums">{k.requests}</span>
                  <span className="text-[10px] text-muted-foreground">pedidos</span>
                </div>
              </div>
              <ul className="min-w-0 flex-1 space-y-1.5 text-xs">
                {services.map((s) => (
                  <li key={s.name} className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
                    <span className="truncate" title={s.name}>
                      {s.name}
                    </span>
                    <span className="ml-auto font-semibold tabular-nums">{s.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>

        <Panel title="Cidades com mais pedidos" subtitle="Onde o veículo estava ao pedir">
          <RankBars data={summary.byCity} color="var(--asst-c4)" empty="Sem dados no período" />
        </Panel>

        <Panel title="Guinchos mais acionados" subtitle="Atendimentos e valor pago no período">
          <RankBars data={summary.byTowTruck} color="var(--asst-c2)" empty="Nenhum guincho acionado no período" showCost />
        </Panel>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel title="Horário dos pedidos" subtitle="Hora de Brasília" className="lg:col-span-2">
          {k.requests === 0 ? (
            <EmptyChart text="Sem dados no período" />
          ) : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hours} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--asst-grid)" />
                  <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} interval={2} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                  <Tooltip content={<CountTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} />
                  <Bar dataKey="count" name="Pedidos" fill="var(--asst-c1)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
        <Panel title="Dia da semana">
          {k.requests === 0 ? (
            <EmptyChart text="Sem dados no período" />
          ) : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekdays} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--asst-grid)" />
                  <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                  <Tooltip content={<CountTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} />
                  <Bar dataKey="count" name="Pedidos" fill="var(--asst-c4)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel
          title="Últimas solicitações"
          className="lg:col-span-2"
          action={
            <Button variant="ghost" size="sm" onClick={() => onGoTo("solicitacoes")}>
              Ver todas
            </Button>
          }
        >
          {summary.recent.length === 0 ? (
            <EmptyChart text="Nenhuma solicitação no período" />
          ) : (
            <ul className="divide-y">
              {summary.recent.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                  <span className="w-28 shrink-0 text-xs tabular-nums text-muted-foreground">{fmtDateTime(r.requestedAt)}</span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{r.serviceTypeName}</span>
                    <span className="text-muted-foreground"> · {r.vehicleName} · {r.clientName}</span>
                  </span>
                  {r.towTruckName && <span className="truncate text-xs text-muted-foreground">🛻 {r.towTruckName}</span>}
                  <StatusBadge status={r.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel
          title="Status"
          subtitle="Solicitações do período"
          action={
            <Button variant="ghost" size="sm" onClick={() => onGoTo("mapa")}>
              Ver mapa
            </Button>
          }
        >
          {statuses.length === 0 ? (
            <EmptyChart text="Sem dados" />
          ) : (
            <ul className="space-y-2">
              {statuses.map((s) => (
                <li key={s.name} className="flex items-center gap-2 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                  {s.label}
                  <span className="ml-auto font-semibold tabular-nums">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
          {summary.trucksByCity.length > 0 && (
            <>
              <h3 className="mb-2 mt-5 text-xs font-semibold text-muted-foreground">Guinchos por cidade</h3>
              <ul className="space-y-1.5 text-sm">
                {summary.trucksByCity.slice(0, 8).map((c) => (
                  <li key={`${c.city}/${c.state}`} className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate">
                      {c.city}/{c.state}
                    </span>
                    <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                      <span className="font-semibold text-foreground">{c.active}</span>/{c.total}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba Solicitações

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative w-full sm:w-72">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-lg border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );
}

function RequestsTab({
  from,
  to,
  access,
  trucks,
  onDispatch,
}: {
  from: string;
  to: string;
  access: AssistanceCenterAccess;
  trucks: TowTruck[];
  onDispatch: (requestId: string) => void;
}) {
  const [status, setStatus] = useState<RequestStatus | undefined>(undefined);
  const [q, setQ] = useState("");
  const { data, isLoading, error } = useAssistanceRequests(from, to, status);
  const [editing, setEditing] = useState<AssistanceRequestRow | null>(null);
  const [canceling, setCanceling] = useState<AssistanceRequestRow | null>(null);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data ?? [];
    return (data ?? []).filter((r) =>
      [r.clientName, r.vehicleName, r.serviceTypeName, r.city, r.towTruckName, r.address].some((v) => v?.toLowerCase().includes(term)),
    );
  }, [data, q]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {[{ key: undefined, label: "Todas" } as { key: RequestStatus | undefined; label: string }, ...REQUEST_STATUSES].map((s) => (
            <button
              key={s.key ?? "all"}
              type="button"
              onClick={() => setStatus(s.key)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
                status === s.key ? "border-foreground bg-foreground text-background" : "bg-background text-muted-foreground hover:bg-muted",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="ml-auto w-full sm:w-auto">
          <SearchBox value={q} onChange={setQ} placeholder="Cliente, veículo, cidade, guincho…" />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        As solicitações são registradas pelo app (Perfil → Registrar Assistência). Aqui você atribui o guincho, o valor e conclui o
        atendimento.
      </p>

      {isLoading ? (
        <LoadingBlock text="Carregando solicitações…" />
      ) : error && !data ? (
        <ErrorBlock text={errorText(error, "Não foi possível carregar as solicitações.")} />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border bg-background p-10 text-center text-sm text-muted-foreground">Nenhuma solicitação encontrada no período.</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-background shadow-sm">
          {/* Celular: cartões. Computador: tabela. */}
          <ul className="divide-y md:hidden">
            {rows.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => setEditing(r)} disabled={!access.requests} className="w-full space-y-1 p-3 text-left disabled:cursor-default">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">
                      {r.serviceTypeName}
                      <PlanTag plan={r.assistancePlan} />
                    </span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {r.vehicleName} · {r.clientName}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>{fmtDateTime(r.requestedAt)}</span>
                    <span className="truncate">{r.city ? `${r.city}${r.state ? `/${r.state}` : ""}` : ""}</span>
                  </div>
                  {(r.towTruckName || r.cost != null) && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="truncate">{r.towTruckName ? `🛻 ${r.towTruckName}` : ""}</span>
                      {r.cost != null && <span className="font-semibold tabular-nums">{fmt(r.cost)}</span>}
                    </div>
                  )}
                </button>
                {access.requests && (
                  <div className="flex gap-2 px-3 pb-3">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => onDispatch(r.id)}>
                      🛻 Guincho
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditing(r)}>
                      Atender
                    </Button>
                    {access.cancel && r.status !== "cancelado" && (
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setCanceling(r)}>
                        Cancelar
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Data</th>
                  <th className="px-3 py-2 font-medium">Serviço</th>
                  <th className="px-3 py-2 font-medium">Cliente / veículo</th>
                  <th className="px-3 py-2 font-medium">Cidade</th>
                  <th className="px-3 py-2 font-medium">Guincho</th>
                  <th className="px-3 py-2 text-right font-medium">Valor</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/30">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">{fmtDateTime(r.requestedAt)}</td>
                    <td className="px-3 py-2 font-medium">
                      {r.serviceTypeName}
                      <PlanTag plan={r.assistancePlan} />
                    </td>
                    <td className="max-w-[260px] px-3 py-2">
                      <div className="truncate">{r.clientName}</div>
                      <div className="truncate text-xs text-muted-foreground">{r.vehicleName}</div>
                    </td>
                    <td className="px-3 py-2">{r.city ? `${r.city}${r.state ? `/${r.state}` : ""}` : <span className="text-muted-foreground">—</span>}</td>
                    <td className="max-w-[180px] truncate px-3 py-2">{r.towTruckName ?? <span className="text-muted-foreground">—</span>}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{r.cost != null ? fmt(r.cost) : "—"}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {access.requests && (
                        <div className="flex justify-end gap-1">
                          <Button variant="outline" size="sm" onClick={() => onDispatch(r.id)} title="Acionar guincho, acompanhar e conversar">
                            🛻 Guincho
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setEditing(r)}>
                            Atender
                          </Button>
                          {access.cancel && r.status !== "cancelado" && (
                            <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setCanceling(r)}>
                              Cancelar
                            </Button>
                          )}
                        </div>
                      )}
                      {r.status === "cancelado" && r.canceledByName && (
                        <div className="mt-1 text-[11px] text-muted-foreground" title={r.cancelReason ?? undefined}>
                          por {r.canceledByName}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && <RequestDialog request={editing} trucks={trucks} canCancel={access.cancel} onClose={() => setEditing(null)} />}
      {canceling && <CancelRequestDialog request={canceling} onClose={() => setCanceling(null)} />}
    </div>
  );
}

const field = "h-10 w-full rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const parseMoney = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t);
  return Number.isFinite(n) ? n : NaN;
};
const moneyStr = (n: number | null) => (n == null ? "" : n.toFixed(2).replace(".", ","));

function CancelRequestDialog({ request, onClose }: { request: AssistanceRequestRow; onClose: () => void }) {
  const cancel = useCancelAssistanceRequest();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Cancelar solicitação?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg bg-muted/40 p-2.5">
            <div className="font-semibold">{request.serviceTypeName}</div>
            <div className="text-muted-foreground">
              {request.vehicleName} · {request.clientName} · {fmtDateTime(request.requestedAt)}
            </div>
          </div>
          <p className="text-muted-foreground">
            Os links enviados aos guinchos param de funcionar e o pedido deixa de contar no limite de uso do serviço deste veículo.
          </p>
          <label className="block space-y-1">
            <span className="text-xs font-semibold">Motivo (opcional)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: cliente desistiu, aberta por engano" className={field} autoFocus />
          </label>
          {error && <p className="text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant="destructive"
            disabled={cancel.isPending}
            onClick={async () => {
              setError(null);
              try {
                await cancel.mutateAsync({ id: request.id, reason: reason.trim() || null });
                onClose();
              } catch (e) {
                setError(errorText(e, "Não foi possível cancelar"));
              }
            }}
          >
            {cancel.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Cancelar solicitação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestDialog({ request, trucks, canCancel, onClose }: { request: AssistanceRequestRow; trucks: TowTruck[]; canCancel: boolean; onClose: () => void }) {
  const [status, setStatus] = useState<RequestStatus>(request.status);
  const [towTruckId, setTowTruckId] = useState(request.towTruckId ?? "");
  const [cost, setCost] = useState(moneyStr(request.cost));
  const [km, setKm] = useState(request.distanceKm == null ? "" : String(request.distanceKm).replace(".", ","));
  const [address, setAddress] = useState(request.address ?? "");
  const [city, setCity] = useState(request.city ?? "");
  const [state, setState] = useState(request.state ?? "");
  const [notes, setNotes] = useState(request.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateAssistanceRequest();

  // Guinchos da mesma cidade primeiro; inativos só se já estavam atribuídos.
  const options = useMemo(() => {
    const c = city.trim().toLowerCase();
    return trucks
      .filter((t) => t.active || t.id === request.towTruckId)
      .sort((a, b) => Number(b.city.toLowerCase() === c) - Number(a.city.toLowerCase() === c) || a.name.localeCompare(b.name));
  }, [trucks, city, request.towTruckId]);
  const chosen = trucks.find((t) => t.id === towTruckId);
  const kmNum = parseMoney(km);
  const estimate = chosen && (chosen.baseFee != null || chosen.pricePerKm != null) && kmNum != null && !Number.isNaN(kmNum)
    ? (chosen.baseFee ?? 0) + (chosen.pricePerKm ?? 0) * kmNum
    : null;

  const save = async () => {
    setError(null);
    const costNum = parseMoney(cost);
    if (Number.isNaN(costNum) || Number.isNaN(kmNum)) {
      setError("Valor ou km inválido");
      return;
    }
    try {
      await update.mutateAsync({
        id: request.id,
        input: {
          status,
          towTruckId: towTruckId || null,
          cost: costNum,
          distanceKm: kmNum,
          address: address.trim() || null,
          city: city.trim() || null,
          state: state.trim() || null,
          notes: notes.trim() || null,
        },
      });
      onClose();
    } catch (e) {
      setError(errorText(e, "Não foi possível salvar"));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Atender solicitação</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border bg-muted/30 p-3 text-sm">
            <div className="font-semibold">{request.serviceTypeName}</div>
            <div className="text-muted-foreground">
              {request.vehicleName} · {request.clientName}
            </div>
            {request.assistancePlan ? (
              <div className="mt-1 text-xs font-semibold text-[var(--asst-done)]">🛡 Veículo com assistência — plano {request.assistancePlan}</div>
            ) : (
              <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">Este veículo não está na lista de assistência (aba Clientes).</div>
            )}
            <div className="mt-1 text-xs text-muted-foreground">
              Pedido em {fmtDateTime(request.requestedAt)} por {request.requestedByName}
            </div>
            {request.reason && <div className="mt-1 text-xs">“{request.reason}”</div>}
            {request.status === "cancelado" && request.canceledAt && (
              <div className="mt-1 text-xs font-semibold text-destructive">
                Cancelada em {fmtDateTime(request.canceledAt)}
                {request.canceledByName && ` por ${request.canceledByName}`}
                {request.cancelReason && ` — ${request.cancelReason}`}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <span className="text-xs font-semibold">Status</span>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
              {REQUEST_STATUSES.filter((s) => canCancel || (s.key !== "cancelado" && request.status !== "cancelado") || s.key === request.status).map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStatus(s.key)}
                  className={cn(
                    "rounded-lg border px-2 py-2 text-xs font-semibold transition-colors",
                    status === s.key ? "border-foreground bg-foreground text-background" : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <label className="block space-y-1">
            <span className="text-xs font-semibold">Guincho</span>
            <select value={towTruckId} onChange={(e) => setTowTruckId(e.target.value)} className={field}>
              <option value="">Nenhum (serviço sem guincho)</option>
              {options.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} — {t.city}/{t.state}
                  {t.available24h ? " · 24h" : ""}
                </option>
              ))}
            </select>
            {chosen && (
              <div className="flex flex-wrap gap-3 pt-1 text-xs text-muted-foreground">
                {chosen.phone && (
                  <a href={`tel:${digits(chosen.phone)}`} className="inline-flex items-center gap-1 hover:text-foreground">
                    <Phone className="h-3.5 w-3.5" /> {chosen.phone}
                  </a>
                )}
                {waLink(chosen.whatsapp ?? chosen.phone) && (
                  <a href={waLink(chosen.whatsapp ?? chosen.phone)!} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--asst-done)] hover:underline">
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                )}
              </div>
            )}
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Distância (km)</span>
              <input value={km} onChange={(e) => setKm(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" placeholder="0" className={cn(field, "tabular-nums")} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Valor pago (R$)</span>
              <input value={cost} onChange={(e) => setCost(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" placeholder="0,00" className={cn(field, "tabular-nums")} />
            </label>
          </div>
          {estimate != null && (
            <button type="button" onClick={() => setCost(moneyStr(estimate))} className="-mt-2 text-xs text-primary hover:underline">
              Pela tabela do guincho: {fmt(estimate)} — usar este valor
            </button>
          )}

          <label className="block space-y-1">
            <span className="text-xs font-semibold">Endereço do atendimento</span>
            <input value={address} onChange={(e) => setAddress(e.target.value)} className={field} />
          </label>
          <div className="grid grid-cols-[1fr_80px] gap-3">
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Cidade</span>
              <input value={city} onChange={(e) => setCity(e.target.value)} className={field} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">UF</span>
              <input value={state} onChange={(e) => setState(e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase())} className={field} />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-xs font-semibold">Observações</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Aba Guinchos

function TowTrucksTab({
  trucks,
  loading,
  error,
  access,
  onNew,
  onEdit,
}: {
  trucks: TowTruck[] | undefined;
  loading: boolean;
  error: unknown;
  access: AssistanceCenterAccess;
  onNew: () => void;
  onEdit: (t: TowTruck) => void;
}) {
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [deleting, setDeleting] = useState<TowTruck | null>(null);
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (trucks ?? []).filter(
      (t) =>
        (!onlyActive || t.active) &&
        (!term || [t.name, t.contactName, t.city, t.state, t.plate, t.phone].some((v) => v?.toLowerCase().includes(term))),
    );
  }, [trucks, q, onlyActive]);

  if (loading) return <LoadingBlock text="Carregando guinchos…" />;
  if (error && !trucks) return <ErrorBlock text={errorText(error, "Não foi possível carregar os guinchos.")} />;
  if (!trucks?.length) {
    return (
      <div className="rounded-2xl border border-dashed bg-background p-10 text-center">
        <div className="text-4xl">🛻</div>
        <h3 className="mt-2 font-semibold">Nenhum guincho cadastrado</h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Cadastre os guinchos parceiros com cidade e telefone. Eles aparecem no mapa e podem ser atribuídos às solicitações.
        </p>
        {access.manage && (
          <Button className="mt-4" onClick={onNew}>
            <Plus className="h-4 w-4" /> Cadastrar guincho
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <SearchBox value={q} onChange={setQ} placeholder="Nome, cidade, placa, telefone…" />
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} className="h-4 w-4 accent-foreground" />
          Só ativos
        </label>
        <span className="ml-auto text-xs text-muted-foreground">
          {list.length} de {trucks.length}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {list.map((t) => {
          const wa = waLink(t.whatsapp ?? t.phone);
          return (
            <article key={t.id} className={cn("flex flex-col rounded-2xl border bg-background p-4 shadow-sm", !t.active && "opacity-60")}>
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[color-mix(in_srgb,var(--asst-c2)_14%,transparent)] text-xl">🛻</span>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-semibold" title={t.name}>
                    {t.name}
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {t.city}/{t.state} · raio {t.coverageRadiusKm} km
                  </div>
                </div>
                {!t.active && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">Inativo</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                <span className="rounded-full bg-muted px-2 py-0.5 font-medium">{truckTypeLabel(t.truckType)}</span>
                {t.plate && <LicensePlate plate={t.plate} size="xs" />}
                {t.available24h && <span className="rounded-full bg-[color-mix(in_srgb,var(--asst-done)_15%,transparent)] px-2 py-0.5 font-semibold text-[var(--asst-done)]">24h</span>}
                {t.traccarDeviceId && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--asst-c1)_15%,transparent)] px-2 py-0.5 font-semibold text-[var(--asst-c1)]">
                    <Radio className="h-3 w-3" /> Rastreado
                  </span>
                )}
                {t.latitude == null && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-700 dark:text-amber-300">Sem localização</span>}
              </div>
              <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-muted/40 p-2">
                  <dt className="text-[10px] text-muted-foreground">Saída</dt>
                  <dd className="text-sm font-semibold tabular-nums">{t.baseFee != null ? fmt(t.baseFee) : "—"}</dd>
                </div>
                <div className="rounded-lg bg-muted/40 p-2">
                  <dt className="text-[10px] text-muted-foreground">Por km</dt>
                  <dd className="text-sm font-semibold tabular-nums">{t.pricePerKm != null ? fmt(t.pricePerKm) : "—"}</dd>
                </div>
                <div className="rounded-lg bg-muted/40 p-2">
                  <dt className="text-[10px] text-muted-foreground">Atendimentos</dt>
                  <dd className="text-sm font-semibold tabular-nums">{t.requestsCount}</dd>
                </div>
              </dl>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3 text-xs">
                {t.contactName && <span className="truncate text-muted-foreground">{t.contactName}</span>}
                <div className="ml-auto flex items-center gap-1">
                  {t.phone && (
                    <a href={`tel:${digits(t.phone)}`} title={t.phone} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted">
                      <Phone className="h-4 w-4" />
                    </a>
                  )}
                  {wa && (
                    <a href={wa} target="_blank" rel="noreferrer" title="WhatsApp" className="grid h-8 w-8 place-items-center rounded-lg text-[var(--asst-done)] hover:bg-muted">
                      <MessageCircle className="h-4 w-4" />
                    </a>
                  )}
                  {access.manage && (
                    <>
                      <button type="button" onClick={() => onEdit(t)} title="Editar" className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => setDeleting(t)} title="Excluir" className="grid h-8 w-8 place-items-center rounded-lg text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
      {deleting && <DeleteTruckDialog truck={deleting} onClose={() => setDeleting(null)} />}
    </div>
  );
}

function DeleteTruckDialog({ truck, onClose, onDeleted }: { truck: TowTruck; onClose: () => void; onDeleted?: () => void }) {
  const del = useDeleteTowTruck();
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Excluir guincho?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">{truck.name}</strong> sai do cadastro e do mapa. As {truck.requestsCount} solicitações atendidas por ele
          continuam no histórico, sem guincho. Para só tirar de uso, edite e desmarque “Ativo”.
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={del.isPending}
            onClick={async () => {
              try {
                await del.mutateAsync(truck.id);
                onClose();
                onDeleted?.();
              } catch (e) {
                setError(errorText(e, "Não foi possível excluir"));
              }
            }}
          >
            {del.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

function TowTruckDialog({ editing, onClose }: { editing: TowTruck | null; onClose: () => void }) {
  const [form, setForm] = useState({
    name: editing?.name ?? "",
    contactName: editing?.contactName ?? "",
    phone: editing?.phone ?? "",
    whatsapp: editing?.whatsapp ?? "",
    document: editing?.document ?? "",
    truckType: (editing?.truckType ?? "plataforma") as TruckType,
    plate: editing?.plate ?? "",
    address: editing?.address ?? "",
    city: editing?.city ?? "",
    state: editing?.state ?? "",
    coverageRadiusKm: String(editing?.coverageRadiusKm ?? 50),
    baseFee: moneyStr(editing?.baseFee ?? null),
    pricePerKm: moneyStr(editing?.pricePerKm ?? null),
    available24h: editing?.available24h ?? false,
    active: editing?.active ?? true,
    notes: editing?.notes ?? "",
  });
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    editing?.latitude != null && editing.longitude != null ? { lat: editing.latitude, lng: editing.longitude } : null,
  );
  const [trackerId, setTrackerId] = useState<number | null>(editing?.traccarDeviceId ?? null);
  const [error, setError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateMsg, setLocateMsg] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const save = useSaveTowTruck();
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const locate = async () => {
    if (!form.city.trim()) {
      setLocateMsg("Informe a cidade primeiro");
      return;
    }
    setLocating(true);
    setLocateMsg(null);
    try {
      const { location } = await gpsDriveAssistanceCenterService.geocode(form.address, form.city, form.state);
      if (location) setCoords(location);
      else setLocateMsg("Endereço não encontrado — arraste o pino no mapa até o lugar certo.");
    } catch (e) {
      setLocateMsg(errorText(e, "Não foi possível localizar agora"));
    } finally {
      setLocating(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (!form.name.trim()) return setError("Informe o nome do guincho/prestador");
    if (!form.city.trim()) return setError("Informe a cidade");
    if (form.state.length !== 2) return setError("Escolha a UF");
    const baseFee = parseMoney(form.baseFee);
    const pricePerKm = parseMoney(form.pricePerKm);
    const radius = Number(form.coverageRadiusKm);
    if (Number.isNaN(baseFee) || Number.isNaN(pricePerKm)) return setError("Valor inválido");
    if (!Number.isInteger(radius) || radius < 1) return setError("Raio de atendimento inválido");
    const input: TowTruckInput = {
      name: form.name.trim(),
      contactName: form.contactName.trim() || null,
      phone: form.phone.trim() || null,
      whatsapp: form.whatsapp.trim() || null,
      document: form.document.trim() || null,
      truckType: form.truckType,
      plate: form.plate.trim().toUpperCase() || null,
      address: form.address.trim() || null,
      city: form.city.trim(),
      state: form.state,
      // Endereço mudou e não clicou em "Localizar": o servidor geocodifica.
      latitude: coords?.lat ?? null,
      longitude: coords?.lng ?? null,
      coverageRadiusKm: radius,
      baseFee,
      pricePerKm,
      available24h: form.available24h,
      active: form.active,
      traccarDeviceId: trackerId,
      notes: form.notes.trim() || null,
    };
    try {
      await save.mutateAsync({ id: editing?.id ?? null, input });
      onClose();
    } catch (e) {
      setError(errorText(e, "Não foi possível salvar"));
    }
  };

  // Mudou cidade/endereço depois de localizar → descarta o pino antigo pra
  // não salvar a coordenada da cidade anterior.
  const addrKey = `${form.address}|${form.city}|${form.state}`;
  const [lastAddrKey, setLastAddrKey] = useState(addrKey);
  useEffect(() => {
    if (addrKey !== lastAddrKey) {
      setLastAddrKey(addrKey);
      setCoords(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addrKey]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar guincho" : "Cadastrar guincho"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1 sm:col-span-2">
              <span className="text-xs font-semibold">Nome do guincho / empresa</span>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex.: Guincho Rápido POA" className={field} autoFocus />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Responsável</span>
              <input value={form.contactName} onChange={(e) => set("contactName", e.target.value)} className={field} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">CPF/CNPJ</span>
              <input value={form.document} onChange={(e) => set("document", e.target.value)} className={field} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Telefone</span>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} inputMode="tel" placeholder="(51) 99999-0000" className={field} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">WhatsApp</span>
              <input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} inputMode="tel" placeholder="Se for outro número" className={field} />
            </label>
          </div>

          <div className="space-y-1">
            <span className="text-xs font-semibold">Tipo</span>
            <div className="flex flex-wrap gap-1.5">
              {TRUCK_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => set("truckType", t.key)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs transition-colors",
                    form.truckType === t.key ? "border-foreground bg-foreground text-background" : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Placa</span>
              <input value={form.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} className={cn(field, "font-mono")} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Taxa de saída (R$)</span>
              <input value={form.baseFee} onChange={(e) => set("baseFee", e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" placeholder="0,00" className={cn(field, "tabular-nums")} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">Valor por km (R$)</span>
              <input value={form.pricePerKm} onChange={(e) => set("pricePerKm", e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" placeholder="0,00" className={cn(field, "tabular-nums")} />
            </label>
          </div>

          <div className="space-y-3 rounded-xl border p-3">
            <span className="text-xs font-semibold">Base / localização</span>
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Endereço</span>
              <input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Rua, número, bairro" className={field} />
            </label>
            <div className="grid grid-cols-[1fr_90px_110px] gap-3">
              <label className="block space-y-1">
                <span className="text-xs text-muted-foreground">Cidade</span>
                <input value={form.city} onChange={(e) => set("city", e.target.value)} className={field} />
              </label>
              <label className="block space-y-1">
                <span className="text-xs text-muted-foreground">UF</span>
                <select value={form.state} onChange={(e) => set("state", e.target.value)} className={field}>
                  <option value="">—</option>
                  {UFS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-xs text-muted-foreground">Raio (km)</span>
                <input value={form.coverageRadiusKm} onChange={(e) => set("coverageRadiusKm", e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" className={cn(field, "tabular-nums")} />
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={locate} disabled={locating}>
                {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
                Localizar no mapa
              </Button>
              <span className="text-xs text-muted-foreground">
                {coords ? `Pino em ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)} — arraste para ajustar` : "Sem localizar, o sistema procura pelo endereço ao salvar."}
              </span>
            </div>
            {locateMsg && <p className="text-xs text-amber-700 dark:text-amber-300">{locateMsg}</p>}
            {coords && <PinPickerMap coords={coords} onChange={setCoords} radiusKm={Number(form.coverageRadiusKm) || 0} />}
          </div>

          <TrackerPicker value={trackerId} onChange={setTrackerId} />

          <div className="flex flex-wrap gap-5">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={form.available24h} onChange={(e) => set("available24h", e.target.checked)} className="h-4 w-4 accent-foreground" />
              Atende 24 horas
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-foreground" />
              Ativo
            </label>
          </div>

          <label className="block space-y-1">
            <span className="text-xs font-semibold">Observações</span>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter className="gap-2">
          {editing && (
            <Button variant="ghost" className="mr-auto text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleting(true)}>
              <Trash2 className="h-4 w-4" /> Excluir guincho
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
      {editing && deleting && (
        <DeleteTruckDialog
          truck={editing}
          onClose={() => setDeleting(false)}
          onDeleted={onClose}
        />
      )}
    </Dialog>
  );
}

// Guincho com rastreador da própria plataforma: aparece no mapa pela
// posição ao vivo em vez da base cadastrada.
function TrackerPicker({ value, onChange }: { value: number | null; onChange: (id: number | null) => void }) {
  const { data: vehicles } = useVehicles();
  const [q, setQ] = useState("");
  const current = vehicles?.find((v) => Number(v.id) === value);
  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    return (vehicles ?? []).filter((v) => v.name.toLowerCase().includes(term) || v.plate?.toLowerCase().includes(term)).slice(0, 8);
  }, [vehicles, q]);

  return (
    <div className="space-y-1">
      <span className="text-xs font-semibold">Rastreador do guincho (opcional)</span>
      {value ? (
        <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
          <Radio className="h-4 w-4 text-[var(--asst-c1)]" />
          <span className="truncate">{current ? `${current.name}${current.plate ? ` · ${current.plate}` : ""}` : `Dispositivo #${value}`}</span>
          <button type="button" onClick={() => onChange(null)} className="ml-auto text-xs text-muted-foreground hover:text-destructive">
            Remover
          </button>
        </div>
      ) : (
        <div className="relative">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar veículo por nome ou placa…" className={field} />
          {matches.length > 0 && (
            <ul className="absolute inset-x-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-lg border bg-background shadow-lg">
              {matches.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(Number(v.id));
                      setQ("");
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  >
                    <span className="truncate">{v.name}</span>
                    {v.plate && <LicensePlate plate={v.plate} size="xs" className="ml-auto" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">Com rastreador, o guincho aparece no mapa onde ele está agora.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mapas

const apiKey = () => (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";

function CoverageCircle({ center, radiusKm, color }: { center: { lat: number; lng: number }; radiusKm: number; color: string }) {
  const map = useGoogleMap();
  useEffect(() => {
    if (!map || radiusKm <= 0) return;
    const circle = new google.maps.Circle({
      map,
      center,
      radius: radiusKm * 1000,
      strokeColor: color,
      strokeOpacity: 0.7,
      strokeWeight: 1.5,
      fillColor: color,
      fillOpacity: 0.08,
      clickable: false,
    });
    return () => circle.setMap(null);
  }, [map, center.lat, center.lng, radiusKm, color]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function PinPickerMap({ coords, onChange, radiusKm }: { coords: { lat: number; lng: number }; onChange: (c: { lat: number; lng: number }) => void; radiusKm: number }) {
  if (!apiKey()) return null;
  return (
    <div className="h-56 overflow-hidden rounded-lg border">
      <APIProvider apiKey={apiKey()}>
        <GoogleMap
          key={`${coords.lat.toFixed(3)}:${coords.lng.toFixed(3)}`}
          mapId={resolveGoogleMapId()}
          styles={hasCustomGoogleMapId() ? undefined : GOOGLE_MAPS_CLEAN_STYLE}
          defaultCenter={coords}
          defaultZoom={radiusKm > 30 ? 9 : 12}
          disableDefaultUI
          zoomControl
          clickableIcons={false}
          gestureHandling="cooperative"
          onClick={(e) => e.detail.latLng && onChange(e.detail.latLng)}
          style={{ height: "100%", width: "100%" }}
        >
          <AdvancedMarker
            position={coords}
            draggable
            onDragEnd={(e) => {
              const p = e.latLng;
              if (p) onChange({ lat: p.lat(), lng: p.lng() });
            }}
          >
            <TruckPin active live={false} />
          </AdvancedMarker>
          <CoverageCircle center={coords} radiusKm={radiusKm} color="#2a78d6" />
        </GoogleMap>
      </APIProvider>
    </div>
  );
}

function TruckPin({ active, live, selected }: { active: boolean; live: boolean; selected?: boolean }) {
  return (
    <div className="relative -translate-y-1/2">
      {live && <span className="absolute inset-0 animate-ping rounded-full bg-[var(--asst-c1)] opacity-40" />}
      <div
        className={cn(
          "relative grid h-10 w-10 place-items-center rounded-full border-2 border-white text-lg shadow-lg transition-transform",
          selected && "scale-125",
        )}
        style={{ background: active ? (live ? "var(--asst-c1)" : "var(--asst-c2)") : "var(--asst-neutral)" }}
      >
        🛻
      </div>
    </div>
  );
}

interface MapTruck {
  truck: TowTruck;
  pos: { lat: number; lng: number };
  live: boolean;
  liveAt: string | null;
}

function FitBounds({ points, sig }: { points: { lat: number; lng: number }[]; sig: string }) {
  const map = useGoogleMap();
  useEffect(() => {
    if (!map || points.length === 0) return;
    if (points.length === 1) {
      map.setCenter(points[0]!);
      map.setZoom(11);
      return;
    }
    const b = new google.maps.LatLngBounds();
    for (const p of points) b.extend(p);
    map.fitBounds(b, 60);
  }, [map, sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function MapTab({ trucks, from, to }: { trucks: TowTruck[]; from: string; to: string }) {
  const { data: vehicles } = useVehicles();
  const { data: requests } = useAssistanceRequests(from, to);
  const [showRequests, setShowRequests] = useState(true);
  const [showCoverage, setShowCoverage] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);

  const byVehicle = useMemo(() => {
    const m = new Map<number, Vehicle>();
    for (const v of vehicles ?? []) m.set(Number(v.id), v);
    return m;
  }, [vehicles]);

  const mapTrucks = useMemo<MapTruck[]>(() => {
    const out: MapTruck[] = [];
    for (const t of trucks) {
      if (onlyActive && !t.active) continue;
      const v = t.traccarDeviceId ? byVehicle.get(t.traccarDeviceId) : undefined;
      if (v && Number.isFinite(v.lat) && Number.isFinite(v.lng) && (v.lat !== 0 || v.lng !== 0)) {
        out.push({ truck: t, pos: { lat: v.lat, lng: v.lng }, live: true, liveAt: v.positionTimestamp ?? v.lastConnection });
      } else if (t.latitude != null && t.longitude != null) {
        out.push({ truck: t, pos: { lat: t.latitude, lng: t.longitude }, live: false, liveAt: null });
      }
    }
    return out;
  }, [trucks, byVehicle, onlyActive]);

  const cities = useMemo(() => {
    const m = new Map<string, MapTruck[]>();
    for (const mt of mapTrucks) {
      const k = `${mt.truck.city}/${mt.truck.state}`;
      m.set(k, [...(m.get(k) ?? []), mt]);
    }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [mapTrucks]);

  const visible = cityFilter ? mapTrucks.filter((mt) => `${mt.truck.city}/${mt.truck.state}` === cityFilter) : mapTrucks;
  const reqPoints = (requests ?? []).filter((r) => r.latitude != null && r.longitude != null);
  const withoutLocation = trucks.filter((t) => (!onlyActive || t.active) && !mapTrucks.some((mt) => mt.truck.id === t.id)).length;
  const sel = mapTrucks.find((mt) => mt.truck.id === selected) ?? null;
  const fitPoints = visible.length ? visible.map((v) => v.pos) : reqPoints.map((r) => ({ lat: r.latitude!, lng: r.longitude! }));

  if (!apiKey()) return <ErrorBlock text="Mapa indisponível (chave do Google Maps não configurada)." />;

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
      <aside className="order-2 space-y-3 lg:order-1">
        <div className="rounded-2xl border bg-background p-3 shadow-sm">
          <div className="space-y-2 text-sm">
            <label className="flex cursor-pointer items-center gap-2">
              <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} className="h-4 w-4 accent-foreground" />
              Só guinchos ativos
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input type="checkbox" checked={showCoverage} onChange={(e) => setShowCoverage(e.target.checked)} className="h-4 w-4 accent-foreground" />
              Mostrar raio de atendimento
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input type="checkbox" checked={showRequests} onChange={(e) => setShowRequests(e.target.checked)} className="h-4 w-4 accent-foreground" />
              Solicitações do período ({reqPoints.length})
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 border-t pt-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--asst-c2)]" /> Base
            </span>
            <span className="flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--asst-c1)]" /> Ao vivo
            </span>
            <span className="flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--asst-open)]" /> Solicitação
            </span>
          </div>
        </div>

        <div className="rounded-2xl border bg-background shadow-sm">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <h3 className="text-sm font-semibold">Guinchos por cidade</h3>
            {cityFilter && (
              <button type="button" onClick={() => setCityFilter(null)} className="text-xs text-primary hover:underline">
                Ver todas
              </button>
            )}
          </div>
          {cities.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">Nenhum guincho com localização.</p>
          ) : (
            <ul className="max-h-[420px] divide-y overflow-y-auto">
              {cities.map(([city, list]) => (
                <li key={city}>
                  <button
                    type="button"
                    onClick={() => setCityFilter(cityFilter === city ? null : city)}
                    className={cn("flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50", cityFilter === city && "bg-muted")}
                  >
                    <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate font-medium">{city}</span>
                    <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums">{list.length}</span>
                  </button>
                  {cityFilter === city && (
                    <ul className="pb-2">
                      {list.map((mt) => (
                        <li key={mt.truck.id}>
                          <button
                            type="button"
                            onClick={() => setSelected(mt.truck.id)}
                            className={cn("flex w-full items-center gap-2 py-1.5 pl-9 pr-3 text-left text-xs hover:bg-muted/50", selected === mt.truck.id && "font-semibold")}
                          >
                            <span className="truncate">{mt.truck.name}</span>
                            {mt.live && <Radio className="ml-auto h-3 w-3 shrink-0 text-[var(--asst-c1)]" />}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
          {withoutLocation > 0 && (
            <p className="border-t px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
              {withoutLocation} guincho(s) sem localização — edite e clique em “Localizar no mapa”.
            </p>
          )}
        </div>
      </aside>

      <div className="order-1 h-[60vh] min-h-[380px] overflow-hidden rounded-2xl border shadow-sm lg:order-2 lg:h-[640px]">
        <APIProvider apiKey={apiKey()}>
          <GoogleMap
            mapId={resolveGoogleMapId()}
            styles={hasCustomGoogleMapId() ? undefined : GOOGLE_MAPS_CLEAN_STYLE}
            defaultCenter={{ lat: -15.78, lng: -47.93 }}
            defaultZoom={4}
            disableDefaultUI
            zoomControl
            fullscreenControl
            clickableIcons={false}
            gestureHandling="greedy"
            onClick={() => setSelected(null)}
            style={{ height: "100%", width: "100%" }}
          >
            <FitBounds points={fitPoints} sig={`${cityFilter ?? "all"}:${visible.length}:${onlyActive}`} />
            {showRequests &&
              reqPoints.map((r) => (
                <AdvancedMarker key={`r-${r.id}`} position={{ lat: r.latitude!, lng: r.longitude! }} title={`${r.serviceTypeName} · ${r.vehicleName} · ${fmtDateTime(r.requestedAt)}`} zIndex={1}>
                  <span className="block h-3.5 w-3.5 rounded-full border-2 border-white shadow" style={{ background: STATUS_COLOR[r.status] ?? "var(--asst-open)" }} />
                </AdvancedMarker>
              ))}
            {visible.map((mt) => (
              <AdvancedMarker key={mt.truck.id} position={mt.pos} onClick={() => setSelected(mt.truck.id)} zIndex={selected === mt.truck.id ? 30 : 10} title={mt.truck.name}>
                <TruckPin active={mt.truck.active} live={mt.live} selected={selected === mt.truck.id} />
              </AdvancedMarker>
            ))}
            {showCoverage &&
              visible.map((mt) => (
                <CoverageCircle key={`c-${mt.truck.id}`} center={mt.pos} radiusKm={mt.truck.coverageRadiusKm} color={mt.live ? "#2a78d6" : "#e0731f"} />
              ))}
            {!showCoverage && sel && <CoverageCircle center={sel.pos} radiusKm={sel.truck.coverageRadiusKm} color="#2a78d6" />}
            {sel && (
              <InfoWindow position={sel.pos} pixelOffset={[0, -44]} onCloseClick={() => setSelected(null)} headerDisabled>
                <TruckInfo mt={sel} />
              </InfoWindow>
            )}
          </GoogleMap>
        </APIProvider>
      </div>
    </div>
  );
}

function TruckInfo({ mt }: { mt: MapTruck }) {
  const t = mt.truck;
  const wa = waLink(t.whatsapp ?? t.phone);
  return (
    <div className="w-60 space-y-1.5 p-1 text-slate-800">
      <div className="text-sm font-bold">{t.name}</div>
      <div className="text-xs text-slate-500">
        {truckTypeLabel(t.truckType)} · {t.city}/{t.state} · raio {t.coverageRadiusKm} km
      </div>
      {mt.live ? (
        <div className="text-xs font-semibold text-blue-600">● Ao vivo{mt.liveAt ? ` · ${fmtDateTime(mt.liveAt)}` : ""}</div>
      ) : (
        t.address && <div className="text-xs text-slate-500">{t.address}</div>
      )}
      <div className="flex gap-3 text-xs">
        {t.baseFee != null && <span>Saída {fmt(t.baseFee)}</span>}
        {t.pricePerKm != null && <span>{fmt(t.pricePerKm)}/km</span>}
        {t.available24h && <span className="font-semibold text-emerald-600">24h</span>}
      </div>
      <div className="flex gap-2 pt-1">
        {t.phone && (
          <a href={`tel:${digits(t.phone)}`} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold">
            <Phone className="h-3 w-3" /> Ligar
          </a>
        )}
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white">
            <MessageCircle className="h-3 w-3" /> WhatsApp
          </a>
        )}
      </div>
    </div>
  );
}
